import { Component, EventEmitter, Input, OnInit, Output, ViewContainerRef } from "@angular/core";
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Observable, debounceTime, distinctUntilChanged, of, startWith, switchMap } from "rxjs";
import { IDetailItem, getEmptyDetailItem } from "./detail-item.interface";
import { Unit } from "../../constants/unit.constants";
import { MyErrorStateMatcher } from "../../util/error-state-matcher";
import { AuthService } from "../../services/auth.service";
import { ProductsService } from "../../../admin/products-page/products/products.service";
import { DialogService } from "../../services/dialog/dialog.service";
import { IConfirmationDialogOptions } from "../../services/dialog/confirmation-dialog/confirmation-dialog-options.interface";

@Component({
  selector: 'app-detail-item',
  templateUrl: './detail-item.component.html',
  styleUrls: ['./detail-item.component.css']
})
export class DetailItemComponent implements OnInit {

  @Input('item') item!: IDetailItem;
  @Input('readOnly') readOnly = false;
  @Input('pricing') pricing = true;
  @Input('disableButton') disableButton = false;
  @Output('itemChange') itemChange = new EventEmitter<any>();
  @Output('remove') remove = new EventEmitter<any>();
  form!: UntypedFormGroup;
  emptyItem = getEmptyDetailItem();
  totalControl = new FormControl();
  unitList: { [key: string]: string } = Unit;
  unitListKeys = Object.keys(Unit);
  customErrorStateMatcher = new MyErrorStateMatcher();

  constructor(
    private _AuthService: AuthService,
    private _ProductsService: ProductsService,
    private fb: UntypedFormBuilder,
    private _dialogService: DialogService,
    private _viewContainerRef: ViewContainerRef,
  ) { }

  ngOnInit(): void {
    this.initializeComponent();
  }

  initializeComponent() {
    if (!this.item) this.item = getEmptyDetailItem();
    this.item.detail = this.item.detail ?? this.item.name ?? this.item.product?.name ?? '';
    this.item.unit = this.item.unit ? this.item.unit : 'unidad';
    this.buildForm();
  }

  get f() { return this.form.controls; }
  buildForm() {
    // console.log(this.item)

    if (this.item.details && this.item.details.length > 0) {
      const taxPercentage = this.item.details[0].taxPercentage;
      const allSame = this.item.details.every(detail => detail.taxPercentage === taxPercentage);

      if (allSame) {
        this.item.taxPercentage = taxPercentage;
      } else {
        this.item.taxPercentage = undefined;
      }
    }

    this.form = this.fb.group({
      serviceAgreement: [this.item.serviceAgreement || undefined],
      task: [this.item.task || undefined],
      product: [this.item.product || undefined],
      detail: [this.item.detail || this.item.name || ""],
      description: [this.item.description || ""],
      quantity: [this.item.quantity ?? this.emptyItem.quantity],
      unit: [this.item.unit || "unidad"],
      cost: [this.item.cost || 0],
      costsubtotal: [this.item.costsubtotal ?? this.emptyItem.costsubtotal],
      price: [this.item.price ?? this.emptyItem.price],
      subtotal: [this.item.subtotal ?? this.emptyItem.subtotal],
      taxPercentage: [this.item.taxPercentage ?? undefined],
      // taxPercentage: [((this.item.task && !this.item.serviceAgreement) || this.item.serviceAgreement) ?
      //   undefined : this.item.taxPercentage ?? this.emptyItem.taxPercentage],
      tax: [this.item.tax ?? this.emptyItem.tax],
      total: [this.item.total ?? this.emptyItem.total]
    });
    this.calculateTotals();
  }

  onSelected(event: any) {
    this.onChanged(event);
  }

  onChanged(event: any) {
    console.log(event.product)
    if (event.product) {
      this.item.product = event.product?._id;
      this.f['product'].setValue(event.product?._id);
      this.f['detail'].setValue(event.text ? event.text : event.product?.name);
      this.f['description'].setValue(event.product?.description ? event.product.description : "");
      this.f['unit'].setValue(event.product?.unit ? event.product.unit : 'unidad');
      this.f['cost'].setValue(event.product?.cost ? event.product.cost : 0);
      this.f['price'].setValue(event.product?.price ? event.product.price : 0);
    } else {
      this.f['detail'].setValue(event.text ?? '');
      // this.f.description.setValue("");
    }
    this.calculateTotals();
  }

  calculateTotals() {
    let costsubtotal = 0;
    let subtotal = 0;
    let tax = 0;
    let total = 0;

    if (this.item.details == undefined || !(this.item.details?.length > 0)) {
      costsubtotal = this.f['quantity'].value * this.f['cost'].value;
      subtotal = this.f['quantity'].value * this.f['price'].value;
      tax = (this.f['quantity'].value * this.f['price'].value) * (this.f['taxPercentage'].value * 0.01);
      total = subtotal + tax;
    } else {
      this.item.details.forEach((det: any) => {
        costsubtotal += det.costsubtotal;
        subtotal += det.subtotal;
        tax += det.tax;
        total += det.total;
      });
    }

    this.f['costsubtotal'].setValue(costsubtotal);
    this.f['subtotal'].setValue(subtotal);
    this.f['tax'].setValue(tax);
    this.f['total'].setValue(total);

    this.onChangeForm();

    this.f['costsubtotal'].setValue(this.formatMoney(costsubtotal));
    this.f['subtotal'].setValue(this.formatMoney(subtotal));
    this.f['tax'].setValue(this.formatMoney(tax));
    this.f['total'].setValue(this.formatMoney(total));
  }

  formatMoney(value: number): string {
    let valueRed = value.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    if (valueRed.length === 7 && value > 0) {
      valueRed = valueRed.substring(0, 1) + '.' + valueRed.substring(1);
    }
    if (valueRed.length === 8 && value < 0) {
      valueRed = valueRed.substring(0, 2) + '.' + valueRed.substring(2);
    }

    return valueRed;
  }

  onChangeForm() {
    /*
    let subtotal = this.f.quantity.value * this.f.price.value;
    let tax = (this.f.quantity.value * this.f.price.value) * (this.f.taxPercentage.value * 0.01);
    let total = subtotal + tax;

    this.item.serviceAgreement = this.f.serviceAgreement.value;
    this.item.task = this.f.task.value;
    this.item.product = this.f.product.value;
    this.item.name = this.f.detail.value;
    this.item.detail = this.f.detail.value;
    this.item.description = this.f.description.value;
    this.item.quantity = this.f.quantity.value;
    this.item.price = this.f.price.value;
    this.item.subtotal = subtotal;
    this.item.taxPercentage = this.f.taxPercentage.value;
    this.item.tax = tax;
    this.item.total = total;
    */
    // console.log(this.form.value);
    Object.keys(this.form.value).forEach((key: any) => {
      const item: any = this.item;
      item[key] = this.form.value[key]
    });
    this.itemChange.emit(this.item);
  }

  removeItem() {
    let itemDetailOrName = this.item?.detail ? this.item?.detail : this.item?.name;

    if (itemDetailOrName) {
      const modalOptions: IConfirmationDialogOptions = {
        title: "Eliminar item",
        message: "¿Seguro desea eliminar " + itemDetailOrName + "? ",
        yesButtonLabel: 'Aceptar', noButtonLabel: 'Cancelar'
      };

      this._dialogService.openConfirmationModal(modalOptions, this._viewContainerRef)
        .subscribe((confirmation: any) => {
          if (confirmation) {
            this.calculateTotals();
            this.remove.emit();
          } else {
            return
          }
        });

    } else {
      this.calculateTotals();
      this.remove.emit();
    }

  }
}